import { createContext } from "react";
import { FeatureAllowDenyListMap } from "src/states/ApplicationStateFeatureMapping";
import { Feature } from "src/states/ApplicationState";
import { GenerateGeom } from "src/managers/GenerateGeom";

export type GeoPoint = { lat: number; lon: number } | { latitude: number; longitude: number } | [number, number];
export type GeoJSONPolygon = any;

export type PLAValueType = "string" | "number" | "m2" | "stk" | "kr" | "date" | "boolean";
export type PLANumberValueType = Extract<PLAValueType, "number" | "m2" | "stk" | "kr">;

export const PLANumberValueTypes: PLANumberValueType[] = ["number", "m2", "stk", "kr"];

export function isPLANumberValueType(valueType: PLAValueType | undefined): valueType is PLANumberValueType {
   return Boolean(valueType && PLANumberValueTypes.includes(valueType as PLANumberValueType));
}

export function getPLAValueTypePostfix(valueType: PLAValueType | undefined): string {
   if (valueType === "m2") return " m2";
   if (valueType === "stk") return " stk";
   if (valueType === "kr") return " kr";
   return "";
}

export interface ConstantAttribute {
   mode: "constant";
   valueType: PLAValueType;
   value: any;
}

export interface CalculatedAttribute {
   mode: "expression";
   expression: string;
}

export type AttributeValue = ConstantAttribute | CalculatedAttribute;

export const PLA_CUBE_TOTAL_KEY = "TOTAL";
export const PLA_CUBE_KEY_SEPARATOR = "\u001f";

export type PLACubeDimensionDefinition = {
   elementTypeId: string;
   expanded?: boolean;
};

export type PLACubeValue = {
   cells?: Record<string, any>;
};

export function getPLACubeCellKey(keys: string[]): string {
   return keys.map((key) => key || PLA_CUBE_TOTAL_KEY).join(PLA_CUBE_KEY_SEPARATOR);
}

export function normalizePLACubeValue(value: any): PLACubeValue {
   return value && typeof value === "object" && !Array.isArray(value) ? { cells: { ...value.cells } } : { cells: {} };
}

export function getPLACubeCellValue(value: any, keys: string[]): any {
   return normalizePLACubeValue(value).cells?.[getPLACubeCellKey(keys)];
}

export function setPLACubeCellValue(value: any, keys: string[], cellValue: any): PLACubeValue {
   const cube = normalizePLACubeValue(value);
   const cells = { ...cube.cells };
   const cellKey = getPLACubeCellKey(keys);
   if (cellValue === undefined || cellValue === null || cellValue === "") delete cells[cellKey];
   else cells[cellKey] = cellValue;
   return { cells };
}

export interface PLAElementRecord {
   recordType: "ELEMENT";
   id: string;
   _version?: number;
   elementType: string;
   name: string;
   sortingNumber?: number;
   geoPoint?: GeoPoint;
   geoShape?: GeoJSONPolygon;
   attributes: Record<string, AttributeValue>;
}

export interface PLARelationRecord {
   recordType: "RELATION";
   id: string;
   _version?: number;
   relationType: string;
   fromId: string;
   toId: string;
}

export type PLARecord = PLAElementRecord | PLARelationRecord;

export interface PLAAlertDefinition {
   booleanExpression: string;
   alertMessage: string;
   optimizerMayOverride?: boolean;
}

export interface PLAAttributeDefinition {
   name: string;
   description?: string;
   type: "constant" | "expression" | "cube";
   valueType?: PLAValueType;
   expression?: string;
   cubeDimensions?: PLACubeDimensionDefinition[];
   hideAsDefault?: boolean;
   sortOrder?: number;
   alerts?: Record<string, PLAAlertDefinition>;
}

export interface PLAElementTypeDefinition {
   name: string;
   sortOrder: number;
   color?: string;
   attributes: Record<string, PLAAttributeDefinition>;
}

export interface PLARelationTypeDefinition {
   name: string;
}

export interface PLAProject {
   id: string;
   name: string;
   description: string;
   active: boolean;
   ownerUser: string;
   ownerOrganization: string;
   allowedUsersWithRoles: { [userId: string]: string[] };
   allowedOrganizationsWithRoles: { [organizationId: string]: string[] };
   settings: any;
   esIndexName: string;
   mainHierarchicalRelationTypeId?: string;
   _version?: number;
   protected: boolean;
   elementTypes: Record<string, PLAElementTypeDefinition>;
   relationTypes: Record<string, PLARelationTypeDefinition>;
}

export const PLADefaultMainHierarchicalRelationTypeReference = "dele";

function normalizePLARelationTypeReference(value: string | undefined) {
   return (value || "").toLowerCase().replace(/[\s_-]+/g, "");
}

export function getDefaultPLAMainHierarchicalRelationTypeId(project: PLAProject): string | undefined {
   const normalizedDefault = normalizePLARelationTypeReference(PLADefaultMainHierarchicalRelationTypeReference);
   return Object.entries(project.relationTypes || {}).find(([relationTypeId, relationType]) => (
      normalizePLARelationTypeReference(relationTypeId) === normalizedDefault || normalizePLARelationTypeReference(relationType.name) === normalizedDefault
   ))?.[0];
}

export function getPLAMainHierarchicalRelationTypeId(project: PLAProject): string | undefined {
   const hasConfiguredRelationTypeId = Object.prototype.hasOwnProperty.call(project, "mainHierarchicalRelationTypeId");
   const configuredRelationTypeId = project.mainHierarchicalRelationTypeId;
   if (hasConfiguredRelationTypeId && !configuredRelationTypeId) return undefined;
   if (configuredRelationTypeId && project.relationTypes?.[configuredRelationTypeId]) return configuredRelationTypeId;
   if (hasConfiguredRelationTypeId) return undefined;
   return getDefaultPLAMainHierarchicalRelationTypeId(project);
}

export function withDefaultPLAMainHierarchicalRelationType(project: PLAProject): PLAProject {
   return Object.prototype.hasOwnProperty.call(project, "mainHierarchicalRelationTypeId") ? project : { ...project, mainHierarchicalRelationTypeId: getDefaultPLAMainHierarchicalRelationTypeId(project) };
}

export function getPLAHierarchicalRelations(project: PLAProject, relations: PLARelationRecord[]): PLARelationRecord[] {
   const mainRelationTypeId = getPLAMainHierarchicalRelationTypeId(project);
   return mainRelationTypeId ? relations.filter((relation) => relation.relationType === mainRelationTypeId) : [];
}

export enum PLAUserAccessRights {
   Read = "PLA",
   Write = "PLAWriteAccess",
   Admin = "PLAAdmin"
}

export const FeaturesAccessiblePLAUserAccessRights: FeatureAllowDenyListMap = {
   [PLAUserAccessRights.Read]: {
      allow: [Feature.PLAProjectRead],
      deny: [Feature.PLAProjectWrite, Feature.PLAProjectAdmin]
   },
   [PLAUserAccessRights.Write]: {
      allow: [Feature.PLAProjectRead, Feature.PLAProjectWrite],
      deny: [Feature.PLAProjectAdmin]
   },
   [PLAUserAccessRights.Admin]: {
      allow: [Feature.PLAProjectRead, Feature.PLAProjectWrite, Feature.PLAProjectAdmin],
      deny: []
   }
};

export interface PLAProjectService {
   loadProjects(): Promise<PLAProject[]>;
   loadProject(projectId: string): Promise<PLAProject>;
   saveProject(project: PLAProject, expectedVersion?: number): Promise<void>;
   deleteProject(projectId: string): Promise<void>;
}

export interface PLADataService {
   loadElements(): Promise<PLAElementRecord[]>;
   loadRelations(): Promise<PLARelationRecord[]>;
   loadRecords(): Promise<PLARecord[]>;
   loadAllRecords(): Promise<PLARecord[]>;
   loadRecord(recordId: string): Promise<PLARecord>;
   saveElement(element: PLAElementRecord, expectedVersion?: number): Promise<void>;
   saveRelation(relation: PLARelationRecord): Promise<void>;
   deleteElement(elementId: string): Promise<void>;
   deleteRelation(relationId: string): Promise<void>;
}

export interface PLAState {
   projects: PLAProject[];
   selectedProject?: PLAProject;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
}

export const PLAContext = createContext<{
   state: PLAState;
   setState: React.Dispatch<React.SetStateAction<PLAState>>;
}>({
   state: { projects: [], elements: [], relations: [] },
   setState: () => undefined
});

export const PLALocalStorageSelectedProjectKey = "PLAState-selectedProjectId";
export const PLAProjectsChangedEvent = "PLAProjectsChanged";

export function notifyPLAProjectsChanged() {
   globalThis.dispatchEvent?.(new Event(PLAProjectsChangedEvent));
}

export function getNextPLAElementTypeColor() {
   return new GenerateGeom().getNextColor() || "#2d8f85";
}

export function createDefaultPLAProject(ownerUser: string, ownerOrganization: string): PLAProject {
   const nextColor = () => getNextPLAElementTypeColor();
   return {
      id: globalThis.crypto?.randomUUID?.() || `pla-project-${Date.now()}`,
      name: "",
      description: "",
      active: true,
      ownerUser,
      ownerOrganization,
      allowedUsersWithRoles: { [ownerUser]: [PLAUserAccessRights.Read, PLAUserAccessRights.Write, PLAUserAccessRights.Admin] },
      allowedOrganizationsWithRoles: {},
      settings: {},
      esIndexName: "",
      mainHierarchicalRelationTypeId: "dele",
      protected: false,
      elementTypes: {
         "projektområde": {
            name: "Projektområde",
            sortOrder: 1,
            color: nextColor(),
            attributes: {
               maxEtageAreal: {
                  name: "Max samlet Etageareal",
                  type: "constant",
                  valueType: "number",
                  alerts: {
                     maxByggeretter: {
                        booleanExpression: '(self | downUntil("dele", "etageAreal") | get("etageAreal") | sum()) > [maxEtageAreal]',
                        alertMessage: "Der bygges for meget"
                     }
                  }
               },
               sumEtageAreal: {
                  name: "Samlet Etageareal",
                  type: "expression",
                  valueType: "number",
                  expression: 'self | downUntil("dele", "etageAreal") | get("etageAreal") | sum()'
               }
            }
         },
         byggefelt: {
            name: "Byggefelt",
            sortOrder: 2,
            color: nextColor(),
            attributes: {
               etageArealSum: {
                  name: "Etageareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: 'self | downUntil("dele", "etageAreal") | get("etageAreal") | sum()'
               }
            }
         },
         bygning: {
            name: "Bygning",
            sortOrder: 3,
            color: nextColor(),
            // Bebygget areal kommer fra BBR.
            // Etagekvadratmeter kommer også fra BBR.
            // Ubebygget areal beregnes som Grundareal fratrukket bebygget areal.
            // Anvendelsen kommer fra BBR.
            attributes: {
               bebyggetAreal: {
                  name: "Bebygget areal",
                  type: "constant",
                  valueType: "number"
               },
               etageAreal: {
                  name: "Etageareal",
                  type: "constant",
                  valueType: "number"
               },
               ubebyggetAreal: {
                  name: "Ubebygget areal",
                  type: "expression",
                  valueType: "number",
                  expression: '(self | downUntil("liggerpå", "regAreal") | get("regAreal") | sum()) - [bebyggetAreal]'
               },
               anvendelse: {
                  name: "Anvendelse",
                  type: "constant",
                  valueType: "string"
               },
               eksisterende: {
                  name: "Eksisterende",
                  type: "constant",
                  valueType: "boolean"
               },
               skalopføres: {
                  name: "Skal opføres",
                  type: "constant",
                  valueType: "boolean"
               },
               skalnedrives: {
                  name: "Skal nedrives",
                  type: "constant",
                  valueType: "boolean"
               }
            }
         },
         bygherre: {
            name: "Bygherre", sortOrder: 4, color: nextColor(), attributes: {
               totalMatrikelareal: {
                  name: "Matrikelareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: 'self | downUntil("ejer", "regAreal") | get("regAreal") | sum()'
               },
               antalBygninger: {
                  name: "Antal bygninger",
                  type: "expression",
                  valueType: "number",
                  expression: 'self | downUntil("ejer", "etageAreal") | get("etageAreal") | count()'
               },
               totalEtageAreal: {
                  name: "Etageareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: 'self | downUntil("ejer", "etageAreal") | get("etageAreal") | sum()'
               }
            }
         },
         ejendom: {
            name: "Ejendom", sortOrder: 5, color: nextColor(), attributes: {
               totalMatrikelareal: {
                  name: "Matrikelareal i alt",
                  type: "expression",
                  valueType: "number",
                  expression: 'self | downUntil("dele", "regAreal") | get("regAreal") | sum()'
               }
            }
         },
         matrikel: {
            name: "Matrikel",
            sortOrder: 6,
            color: nextColor(),
            attributes: {
               matrikelNr: {
                  name: "Matrikelnummer",
                  type: "constant",
                  valueType: "string"
               },
               ejerlavsKode: {
                  name: "Ejerlavskode",
                  type: "constant",
                  valueType: "number"
               },
               regAreal: {
                  name: "Registreret areal",
                  type: "constant",
                  valueType: "number"
               },
               vejAreal: {
                  name: "Vejareal",
                  type: "constant",
                  valueType: "number"
               }
            }
         }
      },
      relationTypes: {
         dele: { name: "Dele" },
         indeholder: { name: "Indeholder" },
         ejer: { name: "Ejer" },
         liggerpå: { name: "Ligger på" }
      }
   };
}

export function getMyPLAAccessRights(project: PLAProject | undefined, userId: string, organizationId: string): string[] {
   if (!project) return [];
   const userRoles = project.allowedUsersWithRoles?.[userId] || [];
   const organizationRoles = project.allowedOrganizationsWithRoles?.[organizationId] || [];
   return Array.from(new Set([...userRoles, ...organizationRoles]));
}

export function hasPLAWriteAccess(project: PLAProject | undefined, userId: string, organizationId: string) {
   const rights = getMyPLAAccessRights(project, userId, organizationId);
   return rights.includes(PLAUserAccessRights.Write) || rights.includes(PLAUserAccessRights.Admin);
}

export function hasPLAAdminAccess(project: PLAProject | undefined, userId: string, organizationId: string) {
   return getMyPLAAccessRights(project, userId, organizationId).includes(PLAUserAccessRights.Admin);
}
